import { useEffect, useMemo, useRef, useState } from 'react'
import { GripVertical } from 'lucide-react'
import {
  DndContext, DragOverlay, KeyboardSensor, PointerSensor, TouchSensor, closestCenter, useSensor, useSensors,
  type DragEndEvent, type DragOverEvent, type DragStartEvent,
} from '@dnd-kit/core'
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Card } from '../types'
import { useLocale } from '../i18n/context'
import { useCardsInfinite, useMoveCard } from '../hooks/useCards'
import { useSwipeNavBlock } from '../hooks/useSwipeNavBlock'
import { relativePlacement } from '../utils/cardOrder'
import { findTermSide } from '../utils/cardSides'
import { valueSide } from '../utils/fieldValue'

/** Rows are all this tall, no exceptions — see the component doc. */
const ROW_HEIGHT = 64
const PAGE_SIZE = 100

interface Props {
  deckId: string
  onClose: () => void
}

/**
 * The Reorder screen (design brief §4.4).
 *
 * Its own screen rather than a mode on the feed, because dragging competes with
 * every other gesture the feed carries. Here there are no others: a row's only
 * action is its handle, so a drag cannot be mistaken for a tap, a long-press, or
 * a scroll.
 *
 * Fixed-height rows, likewise from §4.4 — dragging into a list whose rows are
 * all different heights is a moving target, and the reader is aiming at a slot,
 * not at a card.
 *
 * **Order semantics (ADR-067 Decision 6).** A drop is saved as one
 * `PATCH .../move` placed relative to the neighbour it landed beside, never as a
 * rewrite of the deck's positions. This is what makes the screen safe on a deck
 * it has not fully loaded, which is the failure that disabled it: the old
 * absolute-position request wrote `0..29` over a deck of 1029 whenever the
 * client held one page. A neighbour that is on screen is one the reader can see;
 * the claim "put it after that one" is true or false on its own, and says
 * nothing about the cards below the loaded window. So the screen needs no
 * "load the whole deck first" gate, and there is no window in which it acts on
 * a partial view as though it were the whole one.
 *
 * The screen anchors its own paging at page 1 and pages forward on scroll. That
 * is deliberate: the feed's window can be anchored mid-deck by a deep link, and
 * a card's displayed number here has to be its number in the DECK. Anchored at
 * the start, the position in this list IS the number.
 *
 * Drops are applied one at a time, never concurrently — the server recomputes
 * the whole deck's order from what it reads at the start of each request, so two
 * in flight would read overlapping snapshots. Same reason `useRestoreCardOrder`
 * is sequential.
 */
export default function DeckReorderScreen({ deckId, onClose }: Props) {
  const { tr } = useLocale()
  const dr = tr.deckReorder
  // ADR-106 P3: mounted only while open. On mobile the deck page's history
  // sentinel for this screen also holds a block; the two are independent
  // tokens and each is released on its own.
  useSwipeNavBlock(true, 'deck-reorder-screen')

  const { data, isLoading, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useCardsInfinite(deckId, PAGE_SIZE, 1)
  const moveCard = useMoveCard()

  const serverCards = useMemo(() => data?.pages.flatMap((p) => p.items) ?? [], [data])
  const total = data?.pages[0]?.total ?? 0

  /**
   * The order as the reader has arranged it, held as ids and resolved against
   * the server's cards on every render. It is the truth while the screen is
   * open: every drop is already persisted, so re-reading the server's answer
   * would only risk the list jumping under a thumb that is about to drag again.
   *
   * Ids rather than cards, and merged here rather than in an effect, so a page
   * arriving from the paginator lands at the end (pages arrive in order)
   * without a second render pass to fold it in.
   */
  const [orderIds, setOrderIds] = useState<string[] | null>(null)
  const cards = useMemo(() => {
    if (!orderIds) return serverCards
    const byId = new Map(serverCards.map((c) => [c.id, c]))
    const arranged = orderIds.flatMap((id) => { const c = byId.get(id); return c ? [c] : [] })
    const known = new Set(orderIds)
    return [...arranged, ...serverCards.filter((c) => !known.has(c.id))]
  }, [orderIds, serverCards])

  const [draggingId, setDraggingId] = useState<string | null>(null)
  /** The same fact as `draggingId`, readable synchronously inside a key handler
   * that runs before React has re-rendered (see the Escape handler below). */
  const draggingRef = useRef<string | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  /** Set when a move did not save. Nothing is retried behind the reader's back. */
  const [failed, setFailed] = useState(false)

  /** Serialises the saves. Each drop waits for the previous one to land. */
  const chain = useRef<Promise<unknown>>(Promise.resolve())

  const sensors = useSensors(
    // Handle-only listeners, so the list still scrolls under a finger anywhere
    // else and the drag needs no press-and-hold delay to disambiguate.
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 4 } }),
    // What each handle's label already promises ("drag, or use the arrow
    // keys"): Space or Enter lifts, the arrows move, Space or Enter drops,
    // Escape puts the card back. The drop is saved exactly as a pointer drop is.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  /**
   * The screen's place in the keyboard model. On desktop it is an overlay over
   * the deck screen, and both of that screen's ladders yield to overlays and
   * leave the closing to them (`utils/deckEscapeLadder` rung 1,
   * `utils/deckEnterLadder` rung 1) — so the way out by keyboard is this
   * screen's own, and without it Escape did nothing here at all.
   *
   * Escape while a card is lifted by keyboard belongs to the drag: it puts the
   * card back and leaves the screen open. The listener runs in the capture
   * phase, ahead of the sensor's own, so the drag is still recorded as in
   * progress when this reads it — reading it after the sensor had cancelled
   * would close the screen on the same press that only meant "put it back".
   *
   * Focus moves onto the screen's one way out when it opens, and goes back to
   * where it was when it closes, if that element is still on the page.
   */
  const doneRef = useRef<HTMLButtonElement>(null)
  const onCloseRef = useRef(onClose)
  useEffect(() => { onCloseRef.current = onClose })
  useEffect(() => {
    const returnFocusTo = document.activeElement as HTMLElement | null
    doneRef.current?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented || draggingRef.current !== null) return
      e.preventDefault()
      e.stopPropagation()
      onCloseRef.current()
    }
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('keydown', onKey, true)
      if (returnFocusTo && returnFocusTo.isConnected) returnFocusTo.focus()
    }
  }, [])

  // Infinite scroll — the same sentinel the feed uses.
  const sentinel = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = sentinel.current
    if (!el || !hasNextPage) return
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage() },
      { threshold: 0.1 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  function handleDragStart({ active }: DragStartEvent) {
    draggingRef.current = active.id as string
    setDraggingId(active.id as string)
    setDropIndex(cards.findIndex((c) => c.id === active.id))
  }

  function handleDragOver({ over }: DragOverEvent) {
    if (!over) return
    const index = cards.findIndex((c) => c.id === over.id)
    if (index !== -1) setDropIndex(index)
  }

  function handleDragEnd({ active, over }: DragEndEvent) {
    draggingRef.current = null
    setDraggingId(null)
    setDropIndex(null)
    if (!over || active.id === over.id) return

    const oldIndex = cards.findIndex((c) => c.id === active.id)
    const newIndex = cards.findIndex((c) => c.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const next = arrayMove(cards, oldIndex, newIndex)
    const placement = relativePlacement(next.map((c) => c.id), active.id as string)
    if (!placement) return

    setOrderIds(next.map((c) => c.id))
    const cardId = active.id as string
    chain.current = chain.current
      .then(() => moveCard.mutateAsync({ deckId, cardId, placement }))
      .catch(() => {
        // The list is left showing the arrangement the reader made, which is no
        // longer what the deck holds. Saying so is the whole point: a screen
        // that quietly kept drawing the failed order would be reporting a
        // success it cannot vouch for.
        setFailed(true)
      })
  }

  const draggingCard = draggingId ? cards.find((c) => c.id === draggingId) ?? null : null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="deck-reorder-title"
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        display: 'flex', flexDirection: 'column',
        background: 'var(--bg-page)',
      }}
    >
      {/* Own bar: the title and the one way out (§4.4). */}
      <div
        style={{
          flexShrink: 0, display: 'flex', alignItems: 'center', gap: 8,
          padding: 'calc(env(safe-area-inset-top, 0px) + 10px) 12px 10px 16px',
          background: 'var(--bg-chrome)', borderBottom: '1px solid var(--border)',
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div id="deck-reorder-title" style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>
            {dr.title}
          </div>
          <div style={{ fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-secondary)' }}>
            {dr.subtitle}
          </div>
        </div>
        <button
          ref={doneRef}
          onClick={onClose}
          style={{
            flexShrink: 0, minHeight: 44, padding: '0 12px',
            background: 'transparent', border: 'none', cursor: 'pointer',
            color: 'var(--accent)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 15,
          }}
        >
          {dr.done}
        </button>
      </div>

      {failed && (
        <div
          role="alert"
          style={{
            flexShrink: 0, padding: '10px 16px',
            background: 'var(--danger-soft)',
            borderBottom: '1px solid var(--danger)',
            fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--danger)',
          }}
        >
          {dr.saveFailed}
        </div>
      )}

      <div style={{ flex: 1, overflowY: 'auto', overscrollBehavior: 'contain', padding: '8px 12px 24px' }}>
        {isLoading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>
            {dr.loading}
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={() => { draggingRef.current = null; setDraggingId(null); setDropIndex(null) }}
          >
            <SortableContext items={cards.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              {cards.map((card, index) => (
                <SortableReorderRow
                  key={card.id}
                  card={card}
                  number={index + 1}
                  dropNumber={dropIndex !== null ? dropIndex + 1 : null}
                />
              ))}
            </SortableContext>
            <DragOverlay dropAnimation={{ duration: 160, easing: 'ease' }}>
              {draggingCard ? <ReorderRow card={draggingCard} number={(dropIndex ?? 0) + 1} lifted /> : null}
            </DragOverlay>
          </DndContext>
        )}

        <div ref={sentinel} aria-hidden style={{ height: 1 }} />
        {isFetchingNextPage && (
          <div style={{ padding: '14px 0', textAlign: 'center', fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-secondary)' }}>
            {dr.loadingMore}
          </div>
        )}
        {!isLoading && hasNextPage && !isFetchingNextPage && (
          <div style={{ padding: '10px 0', textAlign: 'center', fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-secondary)' }}>
            {dr.moreBelow(total - cards.length)}
          </div>
        )}
      </div>
    </div>
  )
}

function SortableReorderRow({ card, number, dropNumber }: { card: Card; number: number; dropNumber: number | null }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: card.id })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  // The row the drag came from becomes the landing zone: the slot the card will
  // occupy, and the number it will carry once it does (§4.4).
  if (isDragging) {
    return (
      <div ref={setNodeRef} style={style}>
        <LandingZone number={dropNumber ?? number} />
      </div>
    )
  }
  return (
    <div ref={setNodeRef} style={style}>
      <ReorderRow card={card} number={number} handleProps={{ ...attributes, ...listeners }} />
    </div>
  )
}

function LandingZone({ number }: { number: number }) {
  const { tr } = useLocale()
  return (
    <div
      style={{
        height: ROW_HEIGHT, marginBottom: 6,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        border: '1px dashed var(--accent)', borderRadius: 'var(--radius-sm)',
        background: 'var(--bg-secondary)',
        fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 11.5,
        letterSpacing: '0.06em', textTransform: 'uppercase',
        color: 'var(--accent)',
      }}
    >
      {tr.deckReorder.dropsHere(number)}
    </div>
  )
}

function ReorderRow({
  card, number, handleProps, lifted = false,
}: {
  card: Card
  number: number
  handleProps?: Record<string, unknown>
  lifted?: boolean
}) {
  const { tr } = useLocale()
  const term = findTermSide(card.sides)?.content ?? ''
  // ADR-104: the card's own translation and definition values (first
  // non-blank field of each), so a blank field in front never empties the row.
  const translation = valueSide(card.sides, 'translation')?.content ?? ''
  const definition = valueSide(card.sides, 'definition')?.content ?? ''

  return (
    <div
      style={{
        height: ROW_HEIGHT, marginBottom: 6,
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '0 10px 0 2px',
        background: 'var(--bg-surface)',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
        boxShadow: lifted ? 'var(--shadow-float)' : 'none',
        // Nothing here scrolls or wraps: every row is the same target.
        overflow: 'hidden',
      }}
    >
      <button
        {...handleProps}
        aria-label={tr.deckReorder.handleAria(term || `#${number}`)}
        style={{
          flexShrink: 0, width: 40, height: ROW_HEIGHT,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'transparent', border: 'none', padding: 0,
          color: 'var(--accent)', cursor: 'grab', touchAction: 'none',
        }}
      >
        <GripVertical size={20} strokeWidth={1.9} />
      </button>
      <span
        style={{
          flexShrink: 0, minWidth: 28, textAlign: 'right',
          fontFamily: 'var(--font-ui)', fontVariantNumeric: 'tabular-nums',
          fontSize: 12, color: 'var(--text-secondary)',
        }}
      >
        {number}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
          <span
            style={{
              fontFamily: 'var(--font-content)', fontSize: 15, fontWeight: 600,
              color: 'var(--text-primary)',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}
          >
            {term}
          </span>
          {translation && (
            <span
              style={{
                flexShrink: 1, minWidth: 0,
                fontFamily: 'var(--font-ui)', fontSize: 12.5, color: 'var(--text-secondary)',
                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
              }}
            >
              {translation}
            </span>
          )}
        </div>
        <div
          style={{
            fontFamily: 'var(--font-ui)', fontSize: 12, color: 'var(--text-secondary)',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {definition}
        </div>
      </div>
    </div>
  )
}
